import { apiDownload, apiDownloadNamed, apiFetch, apiUpload } from "@/lib/apiClient";
import { saveBlob } from "@/utils/download";

export const MESSAGE_BODY_MAX = 20_000;

export type BodyFormat = "Markdown" | "Plain";

interface AttachmentApiResponse {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number | string;
  createdAt: string;
  url: string | null;
  urlExpiresAt: string | null;
}

interface CommentApiResponse {
  id: string;
  conversationId: string;
  parentId: string | null;
  rootId: string;
  depth: number | string;
  seq: number | string;
  body: string;
  isInternal: boolean;
  createdAt: string;
  authorId: string | null;
  authorName: string | null;
  editedAt: string | null;
  editCount: number | string;
  isDeleted: boolean;
  replyCount: number | string;
  bodyFormat: string;
  attachments: AttachmentApiResponse[] | null;
}

interface CursorPageResponse {
  items: CommentApiResponse[] | null;
  nextCursor: string | null;
  hasMore: boolean;
  latestSeq: number | string | null;
}

export interface MessageAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  url: string | null;
}

export interface ConversationMessage {
  id: string;
  conversationId: string;
  parentId: string | null;
  rootId: string;
  depth: number;
  seq: number;
  body: string;
  bodyFormat: BodyFormat;
  isInternal: boolean;
  isDeleted: boolean;
  replyCount: number;
  authorId: string | null;
  authorName: string | null;
  createdAt: string;
  editedAt: string | null;
  editCount: number;
  attachments: MessageAttachment[];
}

export interface MessagePage {
  items: ConversationMessage[];
  nextCursor: string | null;
  hasMore: boolean;
  latestSeq: number | null;
}

export interface PostMessageInput {
  body: string;
  parentId?: string | null;
  isInternal?: boolean;
  bodyFormat?: BodyFormat;
}

export interface EditMessageInput {
  body: string;
  bodyFormat?: BodyFormat;
}

export interface MessageEditDraft {
  body: string;
  bodyFormat?: BodyFormat;
  keptAttachmentIds?: string[] | null;
  files: File[];
}

export interface MessageDeleteResult {
  deletedCount: number;
  deletedIds: string[];
  deletedAttachmentIds: string[];
}

interface DeleteMessageApiResponse {
  deletedCount: number | string;
  deletedIds: string[] | null;
  deletedAttachmentIds: string[] | null;
}

function toMessage(c: CommentApiResponse): ConversationMessage {
  return {
    id: c.id,
    conversationId: c.conversationId,
    parentId: c.parentId ?? null,
    rootId: c.rootId,
    depth: Number(c.depth) || 0,
    seq: Number(c.seq) || 0,
    body: c.body ?? "",
    bodyFormat: c.bodyFormat === "Markdown" ? "Markdown" : "Plain",
    isInternal: c.isInternal === true,
    isDeleted: c.isDeleted === true,
    replyCount: Number(c.replyCount) || 0,
    authorId: c.authorId ?? null,
    authorName: c.authorName ?? null,
    createdAt: c.createdAt,
    editedAt: c.editedAt ?? null,
    editCount: Number(c.editCount) || 0,
    attachments: (c.attachments ?? []).map(toAttachment),
  };
}

function toAttachment(a: AttachmentApiResponse): MessageAttachment {
  return {
    id: a.id,
    fileName: a.fileName,
    contentType: a.contentType ?? "",
    sizeBytes: Number(a.fileSizeBytes) || 0,
    url: a.url ?? null,
  };
}

export const conversations = {
  async listMessages(
    conversationId: string,
    params: { limit?: number; before?: string } = {},
  ): Promise<MessagePage> {
    const query = new URLSearchParams();
    if (params.limit) query.set("limit", String(params.limit));
    if (params.before) query.set("before", params.before);
    const qs = query.toString();
    const res = await apiFetch<CursorPageResponse>(
      `/conversations/${encodeURIComponent(conversationId)}/messages${qs ? `?${qs}` : ""}`,
    );
    return {
      items: (res?.items ?? []).map(toMessage),
      nextCursor: res?.nextCursor ?? null,
      hasMore: res?.hasMore === true,
      latestSeq: res?.latestSeq == null ? null : Number(res.latestSeq),
    };
  },

  async postMessage(
    conversationId: string,
    input: PostMessageInput,
  ): Promise<ConversationMessage> {
    const res = await apiFetch<CommentApiResponse>(
      `/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: "POST",
        body: JSON.stringify({
          body: input.body,
          parentId: input.parentId ?? null,
          isInternal: input.isInternal ?? false,
          bodyFormat: input.bodyFormat ?? "Plain",
        }),
      },
    );
    return toMessage(res);
  },

  async postMessageWithFiles(
    conversationId: string,
    input: PostMessageInput,
    files: File[],
  ): Promise<ConversationMessage> {
    const form = new FormData();
    form.append("body", input.body);
    if (input.parentId) form.append("parentId", input.parentId);
    form.append("isInternal", String(input.isInternal ?? false));
    form.append("bodyFormat", input.bodyFormat ?? "Plain");
    for (const file of files) form.append("files", file);

    const res = await apiUpload<CommentApiResponse>(
      `/conversations/${encodeURIComponent(conversationId)}/messages/multipart`,
      form,
    );
    return toMessage(res);
  },

  async editMessage(
    messageId: string,
    input: EditMessageInput,
  ): Promise<ConversationMessage> {
    const res = await apiFetch<CommentApiResponse>(
      `/conversations/messages/${encodeURIComponent(messageId)}`,
      {
        method: "PUT",
        body: JSON.stringify({
          body: input.body,
          bodyFormat: input.bodyFormat ?? "Plain",
        }),
      },
    );
    return toMessage(res);
  },

  async deleteMessage(messageId: string): Promise<MessageDeleteResult> {
    const res = await apiFetch<DeleteMessageApiResponse | null>(
      `/conversations/messages/${encodeURIComponent(messageId)}`,
      { method: "DELETE" },
    );
    return {
      deletedCount: Number(res?.deletedCount) || 0,
      deletedIds: res?.deletedIds ?? [],
      deletedAttachmentIds: res?.deletedAttachmentIds ?? [],
    };
  },

  async saveMessageEdit(
    messageId: string,
    draft: MessageEditDraft,
  ): Promise<ConversationMessage> {
    if (!draft.keptAttachmentIds && draft.files.length === 0) {
      return this.editMessage(messageId, {
        body: draft.body,
        bodyFormat: draft.bodyFormat,
      });
    }

    const form = new FormData();
    form.append("body", draft.body);
    form.append("bodyFormat", draft.bodyFormat ?? "Plain");
    if (draft.keptAttachmentIds) {
      form.append("keptAttachmentIdsJson", JSON.stringify(draft.keptAttachmentIds));
    }
    for (const file of draft.files) form.append("files", file);

    const res = await apiUpload<CommentApiResponse>(
      `/conversations/messages/${encodeURIComponent(messageId)}/multipart`,
      form,
      "PUT",
    );
    return toMessage(res);
  },

  attachmentFile(attachmentId: string) {
    return apiDownloadNamed(`/attachments/${encodeURIComponent(attachmentId)}/download`);
  },

  async downloadAttachment(attachmentId: string, fileName: string) {
    const blob = await apiDownload(`/attachments/${encodeURIComponent(attachmentId)}/download`);
    saveBlob(blob, fileName);
  },
};
